import React, { createContext, useEffect, useMemo, useReducer, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import axiosClient from 'utils/axios';
import { BASE_URL } from 'utils/baseUrlProvider';
// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project import
import { isProduction } from 'utils/helper';

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
};

// Function to track user activity and reset inactivity timer
const trackUserActivity = (callback) => {
  const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

  events.forEach((event) => window.addEventListener(event, callback));

  return () => {
    events.forEach((event) => window.removeEventListener(event, callback));
  };
};
// Verify if token is valid
const verifyToken = (token) => {
  if (!token) return false;

  const decoded = jwtDecode(token);
  return decoded.exp > Date.now() / 1000;
};

// Set session tokens in storage and axios headers
const setSession = (accessToken, refreshToken = null) => {
  if (accessToken) {
    localStorage.setItem('authToken', accessToken);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    axiosClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    delete axiosClient.defaults.headers.common['Authorization'];
  }
};

// Create context
const JWTContext = createContext(null);

let refreshTimer = null;

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [roles, setRoles] = useState([]);

  // Function to decode token and set roles
  const decodeToken = (token) => {
    try {
      const decoded = jwtDecode(token);
      console.log("role", decoded.roles)
      setRoles((decoded?.roles || [])
        .map((role) => role.trim()));
      return decoded;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  // Function to refresh access token
  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token available.');

      const response = await axiosClient.post(`${BASE_URL}/users/refresh/`, {
        refresh: refreshToken,
      });

      const { access } = response.data;

      setSession(access, refreshToken);

      const decoded = decodeToken(access);
      const expiresAt = decoded.exp * 1000;
      scheduleTokenRefresh(expiresAt);
      return true;
    } catch (error) {
      console.error('Error refreshing access token:', error);
      logout();
      return false;
    }
  };

  // Schedule token refresh
  const scheduleTokenRefresh = (expiresAt) => {
    if (refreshTimer) clearTimeout(refreshTimer);

    const currentTime = Date.now();
    const timeUntilRefresh = expiresAt - currentTime - 5 * 60 * 1000;

    if (timeUntilRefresh > 0) {
      refreshTimer = setTimeout(refreshAccessToken, timeUntilRefresh);
    } else {
      refreshAccessToken();
    }
  };

  // Initialize session
  useEffect(() => {
    const init = async () => {
      try {
        const accessToken = localStorage.getItem('authToken');
        const refreshToken = localStorage.getItem('refreshToken');

        if (accessToken && verifyToken(accessToken)) {
          setSession(accessToken, refreshToken);
          const response = await axiosClient.get(`${BASE_URL}/users/`);
          const user = response.data;

          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user,
            },
          });

          // Decode access token and schedule refresh
          const decoded = decodeToken(accessToken);
          const expiresAt = decoded.exp * 1000;
          scheduleTokenRefresh(expiresAt);
        } else if (refreshToken) {
          // Try refreshing the token if access token is invalid
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            const response = await axiosClient.get(`${BASE_URL}/users/`);
            dispatch({
              type: LOGIN,
              payload: {
                isLoggedIn: true,
                user: response.data,
              },
            });
          }
        } else {
          logout();
        }
      } catch (error) {
        console.error('Error initializing session:', error);
        logout();
      }
    };

    init();

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isLoggedIn]);

  useEffect(() => {
    let inactivityTimer;
    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimer);
      if (isProduction) {
        const hasExtendedTimeout = roles.includes('doctor') || roles.includes('optometrist') || roles.includes('nurse');
        const timeoutDuration = hasExtendedTimeout ? 60 * 60 * 1000 : 10 * 60 * 1000;
        inactivityTimer = setTimeout(() => {
          logout();
        }, timeoutDuration);
      }
    };

    if (isProduction) {
      const cleanup = trackUserActivity(resetInactivityTimer);
      resetInactivityTimer();
      return () => {
        clearTimeout(inactivityTimer);
        cleanup();
      };
    }
  }, [roles]);

  const login = async (user) => {
    try {
      if (!user?.authToken) throw new Error("Invalid auth token");
      setSession(user.authToken, user.refreshToken);
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user,
        },
      });

      // Decode access token and set timer
      const decoded = decodeToken(user.authToken);
      if (!decoded) throw new Error("Token decoding failed");
      const expiresAt = decoded.exp * 1000;
      scheduleTokenRefresh(expiresAt);
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const logout = () => {
    setSession(null);
    if (refreshTimer) clearTimeout(refreshTimer);
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    dispatch({ type: LOGOUT });
  };

  const isAdmin = useMemo(() => roles.includes('admin') || roles.includes('superuser'), [roles]);
  const isDoctor = useMemo(() => roles.includes('doctor'), [roles]);
  const isNurse = useMemo(() => roles.includes('nurse'), [roles]);
  const isOptometrist = useMemo(() => roles.includes('optometrist'), [roles]);
  const isReceptionist = useMemo(() => roles.includes('receptionist'), [roles]);
  const isPharmacyStoreManager = useMemo(() => roles.includes('pharmacy_store_manager'), [roles]);
  const isAccountManager = useMemo(() => roles.includes('account_manager'), [roles]);
  const isITTechnician = useMemo(() => roles.includes('it_technician'), [roles]);
  const isLabBiller = useMemo(() => roles.includes('lab_billing'), [roles]);
  const isSeniorLabTechnician = useMemo(() => roles.includes('senior_lab_technician'), [roles]);
  const isLabTechnician = useMemo(() => roles.includes('lab_technician'), [roles]);
  const isLabCollectionStaff = useMemo(() => roles.includes('lab_collection_staff'), [roles]);
  const isPharmacyBiller = useMemo(() => roles.includes('pharmacy_biller'), [roles]);
  const isRadiology = useMemo(() => roles.includes('radiologist'), [roles]);
  const isPatient = useMemo(() => roles.length === 1 && roles[0].toLowerCase() === 'patient', [roles]);

  // Get allowed routes based on role
  const allowedRoutes = useMemo(() => {

    const routesSet = new Set();

    // Shared IPD form/report writer is available to every non-patient authenticated user.
    if (!isPatient) {
      routesSet.add('ipd/report-writer');
    }

    if (isPatient) {
      routesSet.add('users');
      routesSet.add('user-list');
    }

    if (isAdmin) {
      ['admin', 'users', 'catalog', 'medicines', 'reception', 'account', 'invoice', 'labs', 'inventory', 'doctor', 'radiology', 'ipd', 'vaccination', 'insurance'].forEach(route =>
        routesSet.add(route)
      );
      routesSet.add('sale-bills');
      routesSet.add('appointments');
      routesSet.add('crm');
      routesSet.add('account/daily-cash-collection');
      routesSet.add('account/total-collection');
    }

    if (isDoctor || isOptometrist || isNurse) {
      routesSet.add('appointments');
      routesSet.add('doctor');
    }

    if (isDoctor) {
      routesSet.add('ipd');
      routesSet.add('ipd/dashboard');
      routesSet.add('ipd/discharge');
      routesSet.add('ipd/admission');
    }

    if (isLabBiller || isAccountManager || isPharmacyBiller) {
      routesSet.add('ipd');
      routesSet.add('ipd/discharge');
    }

    if (isDoctor || isNurse) {
      routesSet.add('vaccination');
    }

    // Allow doctors and receptionists to view users page (view-only handled in UI)
    if (isDoctor) {
      routesSet.add('users');
    }

    if (isReceptionist) {
      routesSet.add('reception');
      routesSet.add('account');
      routesSet.add('account/total-collection');
      routesSet.add('users');
      routesSet.add('invoice');
      routesSet.add('insurance');
      routesSet.add('insurance/registration');
      routesSet.add('insurance/providers');
      routesSet.add('crm');
    }

    if (isLabBiller) {
      routesSet.add('ipd');
      routesSet.add('invoice');
      routesSet.add('invoice/lab-invoice');
      routesSet.add('invoice/list');
      routesSet.add('account');
      routesSet.add('account/daily-cash-collection');
    }

    if (isPharmacyStoreManager) {
      routesSet.add('catalog');
      routesSet.add('medicines');
      routesSet.add('inventory');
      routesSet.add('sale-bills');
    }

    if (isAccountManager) {
      routesSet.add('inventory');
      routesSet.add('reception');
      routesSet.add('account');
      routesSet.add('account/daily-cash-collection');
      routesSet.add('account/total-collection');
      routesSet.add('sale-bills');
      // Allow viewing past invoices but not creating them
      routesSet.add('invoice');
      routesSet.add('invoice/list');
    }

    if (isITTechnician) {
      routesSet.add('users');
    }

    if (isSeniorLabTechnician || isDoctor) {
      routesSet.add('labs/lab-test-list');
      routesSet.add('labs/lab-management');
      routesSet.add('labs/report');
    }

    if (isLabTechnician) {
      routesSet.add('labs/lab-management');
      routesSet.add('labs/report');
    }

    if (isLabCollectionStaff) {
      routesSet.add('labs/lab-management');
    }

    if (isRadiology) {
      routesSet.add('radiology');
    }
    if (isPharmacyBiller) {
      routesSet.add('invoice');
      routesSet.add('invoice/pharmacy-invoice');
      routesSet.add('invoice/list');
      routesSet.add('account');
      routesSet.add('account/daily-cash-collection');
      routesSet.add('inventory');
      routesSet.add('sale-bills');
    }
    return Array.from(routesSet);
  }, [isAdmin, isDoctor, isNurse, isOptometrist, isReceptionist, isPharmacyStoreManager, isAccountManager, isITTechnician, isLabBiller, isSeniorLabTechnician, isLabTechnician, isLabCollectionStaff, isPharmacyBiller, isRadiology, isPatient]);

  // Check if route is accessible
  const canAccess = (routeId) => {
    if (allowedRoutes.includes(routeId)) {
      return true;
    }
    return allowedRoutes.some(route => routeId.startsWith(route + '/') || route.startsWith(routeId + '/'));
  };

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
        isAdmin,
        isAccountManager,
        isPatient,
        roles,
        canAccess
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

export default JWTContext;
